import { FC, useMemo, useState } from "react";
import { Tab, Tabs } from "@mui/material";
import { useTranslation } from "react-i18next";

import useStyles from "./styles";
import { Sidebar } from "../../containers/Sidebar/Sidebar";
import { Icon } from "../../components/Icon/Icon";
import { colors } from "../../styles/colors";
import BillingSettingsTab from "./BillingSettingsTab";
import BillingProviderTab from "./BillingProviderTab";
import BillingPricingTab from "./BillingPricingTab";
import BillingFeaturesTab from "./BillingFeaturesTab";
import BillingPromoTab from "./BillingPromoTab";
import { AccessDenied } from "../../auth/RequirePermission";
import { Permission } from "../../auth/permissions";
import { useAuth } from "../../auth/useAuth";

/**
 * Billing administration.
 *
 * Three concerns that previously lived outside the product: the free-trial
 * length (an env var), promo codes (the Stripe Dashboard) and plan pricing
 * (an endpoint that desynchronised from Stripe).
 */
export const Billing: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { can } = useAuth();
  const [tab, setTab] = useState(0);

  /*
   * Tabs are built from a declaration rather than hard-coded, so a role that
   * may audit tariffs but not touch trial settings or promo codes gets a
   * coherent screen instead of tabs that render a denial panel. The pricing
   * tab needs only `pricing.view`; the write capability is checked inside it.
   */
  const tabs = useMemo(
    () =>
      (
        [
          {
            permission: "billing.settings.update",
            label: t("billing.tabSettings", "Essai & options"),
            render: () => <BillingSettingsTab />,
          },
          {
            // First after the trial settings, not last: which provider is
            // collecting the money is the frame for everything on the other
            // tabs. Promo codes in particular only apply while a provider that
            // supports them is active.
            permission: "billing.settings.view",
            label: t("billing.tabProvider", "Prestataire"),
            render: () => <BillingProviderTab />,
          },
          {
            permission: "billing.pricing.view",
            label: t("billing.tabPricing", "Tarifs"),
            render: () => <BillingPricingTab />,
          },
          {
            // Sits next to pricing on purpose: what an offer costs and what it
            // includes are the same commercial decision, and splitting them
            // across screens is how the two drifted apart in the first place.
            permission: "billing.plan.view",
            label: t("billing.tabFeatures", "Fonctionnalites"),
            render: () => <BillingFeaturesTab />,
          },
          {
            permission: "billing.promo.view",
            label: t("billing.tabPromo", "Codes promo"),
            render: () => <BillingPromoTab />,
          },
        ] as { permission: Permission; label: string; render: () => JSX.Element }[]
      ).filter((entry) => can(entry.permission)),
    [can, t]
  );

  // Guards against a stale index if the visible set ever shrinks.
  const activeTab = Math.min(tab, Math.max(tabs.length - 1, 0));

  return (
    <Sidebar
      title={t("billing.title", "Paiements")}
      styles={{
        sidebarRightRoot: classes.billingRoot,
        sidebarRoot: classes.contentMain,
      }}
    >
      <div className={classes.contentWrapper}>
        <div className={classes.containerHeader}>
          <div className={classes.divIconArrow}>
            <div className={classes.iconCircle}>
              {/* `coinEuro` rather than `settings`: only some Icon cases
                  forward the `color` prop, and `settings` is not one of them —
                  it would render dark on the blue circle. */}
              <Icon
                name="coinEuro"
                width={28}
                height={28}
                color={colors.WHITE}
              />
            </div>
            <h2>{t("billing.title", "Gestion des paiements")}</h2>
          </div>
        </div>

        {tabs.length === 0 ? (
          <AccessDenied permission="billing.plan.view" />
        ) : (
          <>
            <Tabs
              className={classes.tabs}
              value={activeTab}
              onChange={(_, value) => setTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {tabs.map((entry) => (
                <Tab key={entry.permission} label={entry.label} />
              ))}
            </Tabs>

            {/* Unmounted rather than hidden, so each tab refetches on entry and
                never shows a stale price or redemption count. */}
            {tabs[activeTab]?.render()}
          </>
        )}
      </div>
    </Sidebar>
  );
};

export default Billing;
